import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, ArrowDownWideNarrow, ArrowUpNarrowWide, SlidersVertical, Lock } from 'lucide-react';
import { getStateAbbreviation, extractState } from '../../constants/locations';
import { getSortOptions, defaultSort } from '../../utils/sorting';
import DataSelector from '../DataSelector/DataSelector';
import ExportActions from '../ExportActions/ExportActions';
import './TableHeader.css';

const TableHeader = ({ 
  isHistorical,
  setIsHistorical,
  selectedData,
  setSelectedData,
  showHistoricalToggle = true,
  showDataSelector = true,
  showExportActions = true,
  showSortOptions = false,
  sortConfig,
  onSortChange,
  locationType = '',
  selectedCrime = 'all_crime',
  activeTab,
  setActiveTab,
  selectedView,
  onMapRangeChange,
  compareMode,
  onCompareModeChange,
  rateViewMode,
  setRateViewMode,
  selectedLocation,
  view,
  userPlan,
  onRequestUpgrade,
  exportContext,
}) => {
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showStateRateMenu, setShowStateRateMenu] = useState(false);
  const [showNationalRateMenu, setShowNationalRateMenu] = useState(false);

  const isCounty = locationType === 'county';
  const isCity = locationType === 'city';
  const isMetro = locationType === 'metro';
  const isCountyOrCity = isCounty || isCity;
  const locationPrefix = locationType ? locationType.charAt(0).toUpperCase() + locationType.slice(1) : 'Location';
  const crimePrefix = selectedCrime.toLowerCase();

  const stateAbbr = useMemo(() => {
    if (!isCountyOrCity || !selectedLocation?.info) {
      return getStateAbbreviation(selectedLocation);
    }

    const info = selectedLocation.info;
    if (info.name?.includes(',')) {
      const stateName = info.name.split(',')[1]?.trim();
      return getStateAbbreviation(stateName);
    }
    if (info.state) {
      return getStateAbbreviation(info.state);
    }
    if (selectedLocation.data?.current?.state) {
      return getStateAbbreviation(selectedLocation.data.current.state);
    }
    const allSt = info.name?.match(/^All\s+([A-Z]{2})\s+/i);
    if (allSt) {
      return allSt[1].toUpperCase();
    }
    const extracted = extractState(info);
    if (extracted) {
      if (extracted.length === 2) return extracted.toUpperCase();
      const titled = extracted.charAt(0).toUpperCase() + extracted.slice(1);
      return getStateAbbreviation(titled);
    }

    return getStateAbbreviation(selectedLocation);
  }, [isCountyOrCity, selectedLocation]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.sort-menu') && !event.target.closest('.sort-button')) {
        setShowSortMenu(false);
      }
      if (!event.target.closest('.state-rate-menu') && !event.target.closest('.state-button')) {
        setShowStateRateMenu(false);
      }
      if (!event.target.closest('.national-rate-menu') && !event.target.closest('.national-button')) {
        setShowNationalRateMenu(false);
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside, true);
    
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, []);

  const effectiveCompareMode = useMemo(() => {
    if (!isCountyOrCity) return null;
    
    if (activeTab === 'rates' && rateViewMode) {
      return {
        type: rateViewMode.rateType || (isCounty ? 'county' : 'city'),
        scope: rateViewMode.rateScope || 'state'
      };
    }
    
    return compareMode; 
  }, [isCountyOrCity, isCounty, activeTab, compareMode, rateViewMode]);
  
  const sortOptions = useMemo(() => {
    const selectedSource = exportContext?.selectedSource;
    if (activeTab === 'rates' && (isCountyOrCity || isMetro)) {
      return getSortOptions(
        activeTab, 
        locationPrefix, 
        crimePrefix, 
        selectedCrime, 
        null, 
        isCountyOrCity, 
        stateAbbr,
        rateViewMode,
        selectedSource,
      );
    }
    
    return getSortOptions(
      activeTab, 
      locationPrefix,
      crimePrefix, 
      selectedCrime, 
      effectiveCompareMode, 
      isCountyOrCity, 
      stateAbbr,
      rateViewMode,
      selectedSource,
    );
  }, [activeTab, locationPrefix, crimePrefix, selectedCrime, effectiveCompareMode, isCountyOrCity, isMetro, stateAbbr, rateViewMode, exportContext?.selectedSource]);

  const getCurrentSortLabel = () => {
    const currentOption = sortOptions.find(opt => opt.key === sortConfig.key);
    if (currentOption) return currentOption.label;
    
    const defaultKey = defaultSort(
      selectedCrime, 
      locationType, 
      effectiveCompareMode, 
      rateViewMode
    )[activeTab].key;
    const defaultOption = sortOptions.find(opt => opt.key === defaultKey);
    return defaultOption?.label || '';
  };

  const handleSortClick = (key) => {
    let direction = sortConfig.direction;
    if (key === sortConfig.key) {
      direction = direction === 'asc' ? 'desc' : 'asc';
    } else {
      direction = (key === 'name' || key.includes('rank')) ? 'asc' : 'desc';
    }
    onSortChange({ key, direction });
  };

  const getSortDirectionIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' 
      ? <ArrowUpNarrowWide size={16} /> 
      : <ArrowDownWideNarrow size={16} />;
  };

  const isSingleJurisdiction = useMemo(() => {
    if (!isCountyOrCity || !selectedLocation?.data?.current) return false;

    const data = selectedLocation.data.current;
    const totalInState = locationType === 'city' 
      ? data.total_cities_state 
      : data.total_counties_state;

    return totalInState === 1;
  }, [isCountyOrCity, selectedLocation, locationType]);

  const showCompareToggle = activeTab !== 'reports' && 
                            activeTab !== 'rates' && 
                            view === 'locationName' && 
                            isCountyOrCity && 
                            !isSingleJurisdiction;
  const showRateCompareToggle = activeTab === 'rates' && view === 'locationName' && (isCountyOrCity || isMetro);

  const handleRateViewModeChange = (type, scope) => {
    setRateViewMode({
      rateType: type,
      rateScope: scope
    });

    if (onCompareModeChange && scope !== compareMode) {
      onCompareModeChange(scope);
    }

    setShowStateRateMenu(false);
    setShowNationalRateMenu(false);
  };

  return (
    <div className={`table-controls ${view === 'locationType' && activeTab === 'reports' ? 'location-type-reports-view' : ''}`}>
      {showHistoricalToggle && (
        <button 
          className={`historical-toggle ${isHistorical ? 'active' : ''}`}
          onClick={() => {
            if (userPlan === 'free') {
              onRequestUpgrade?.('historical_data');
              return;
            }
            if (activeTab === 'reports') {
              setIsHistorical(true);
              setActiveTab('totals');
            } else {
              setIsHistorical(!isHistorical);
            }
          }}
        >
          {userPlan === 'free' ? (
            <div className="historical-toggle-inner">
              <div className="historical-toggle-text">
                <span>Historical</span>
                <span>Data</span>
              </div>
              <Lock size={13} className="historical-lock-icon" />
            </div>
          ) : (
            <>
              <span>Historical</span>
              <span>Data</span>
            </>
          )}
        </button>
      )}

      {showCompareToggle && (
        <div className="compare-toggle">
          <button 
            className={`compare-button state-button ${compareMode === 'state' ? 'active' : ''}`}
            onClick={() => onCompareModeChange('state')}
          >
            {stateAbbr}
          </button>
          <button 
            className={`compare-button national-button ${compareMode === 'national' ? 'active' : ''}`}
            onClick={() => onCompareModeChange('national')}
          >
            US
          </button>
        </div>
      )}

      {showRateCompareToggle && (
        <div className="compare-toggle">
          {isMetro ? (
            <>
              <button 
                className={`compare-button state-button ${rateViewMode.rateType === 'metro' ? 'active' : ''}`}
                onClick={() => handleRateViewModeChange('metro', 'national')}
              >
                Metro
              </button>
              <button 
                className={`compare-button national-button ${rateViewMode.rateType === 'state' ? 'active' : ''}`}
                onClick={() => handleRateViewModeChange('state', 'national')}
              >
                National
              </button>
            </>
          ) : (
            <>
              {!isSingleJurisdiction && (
                <div className="dropdown-container rate-dropdown">
                  <button 
                    className={`compare-button state-button ${!isSingleJurisdiction ? 'disabled' : ''} ${rateViewMode.rateScope === 'state' ? 'active' : ''} ${showStateRateMenu ? 'menu-open' : ''}`}
                    onClick={() => {
                      setShowStateRateMenu(!showStateRateMenu);
                      setShowNationalRateMenu(false);
                    }}
                  >
                    <span>{stateAbbr}</span>
                    <ChevronDown size={16} className="chevron-icon" />
                  </button>
                  {showStateRateMenu && (
                    <div className="dropdown-menu state-rate-menu">
                      <button 
                        className={`dropdown-item ${rateViewMode.rateType === locationType && rateViewMode.rateScope === 'state' ? 'active' : ''}`}
                        onClick={() => handleRateViewModeChange(locationType, 'state')}
                        disabled={rateViewMode.rateType === locationType && rateViewMode.rateScope === 'state'}
                      >
                        {stateAbbr} {locationPrefix}
                      </button>
                      <button 
                        className={`dropdown-item ${((isCounty ? rateViewMode.rateType === 'all_counties' : rateViewMode.rateType === 'all_cities') || (rateViewMode.rateType === 'state' && rateViewMode.rateScope === 'state')) && rateViewMode.rateScope === 'state' ? 'active' : ''}`}
                        onClick={() =>
                          handleRateViewModeChange(isCounty ? 'all_counties' : 'all_cities', 'state')
                        }
                        disabled={
                          ((isCounty ? rateViewMode.rateType === 'all_counties' : rateViewMode.rateType === 'all_cities') &&
                            rateViewMode.rateScope === 'state') ||
                          (rateViewMode.rateType === 'state' && rateViewMode.rateScope === 'state')
                        }
                      >
                        {stateAbbr} State
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              <div className="dropdown-container rate-dropdown">
                <button 
                  className={`compare-button national-button ${isSingleJurisdiction ? 'single-jurisdiction' : ''} ${rateViewMode.rateScope === 'national' ? 'active' : ''} ${showNationalRateMenu ? 'menu-open' : ''}`}
                  onClick={() => {
                    setShowNationalRateMenu(!showNationalRateMenu);
                    setShowStateRateMenu(false);
                  }}
                >
                  <span>US</span>
                  <ChevronDown size={16} className="chevron-icon" />
                </button>
                {showNationalRateMenu && (
                  <div className="dropdown-menu national-rate-menu">
                    <button 
                      className={`dropdown-item ${rateViewMode.rateType === locationType && rateViewMode.rateScope === 'national' ? 'active' : ''}`}
                      onClick={() => handleRateViewModeChange(locationType, 'national')}
                      disabled={rateViewMode.rateType === locationType && rateViewMode.rateScope === 'national'}
                    >
                      US {locationPrefix}
                    </button>
                    <button 
                      className={`dropdown-item ${((isCounty ? rateViewMode.rateType === 'all_counties' : rateViewMode.rateType === 'all_cities') || (rateViewMode.rateType === 'national' && rateViewMode.rateScope === 'national')) && rateViewMode.rateScope === 'national' ? 'active' : ''}`}
                      onClick={() =>
                        handleRateViewModeChange(isCounty ? 'all_counties' : 'all_cities', 'national')
                      }
                      disabled={
                        ((isCounty ? rateViewMode.rateType === 'all_counties' : rateViewMode.rateType === 'all_cities') &&
                          rateViewMode.rateScope === 'national') ||
                        (rateViewMode.rateType === 'national' && rateViewMode.rateScope === 'national')
                      }
                    >
                      US National
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {showSortOptions && (
        <div className="dropdown-container">
          <button 
            className={`sort-button ${showSortMenu ? 'active' : ''}`}
            onClick={() => setShowSortMenu(!showSortMenu)}
          >
            <span>
              <SlidersVertical size={16} />
              <span className="sort-title"><strong>Sort By:</strong></span> 
              <span className="sort-option">{getCurrentSortLabel()}</span>
              <span className="sort-option-icon">{getSortDirectionIcon(sortConfig.key)}</span>
            </span>
            <ChevronDown size={16} className="chevron-icon" />
          </button>
          {showSortMenu && (
            <div className="dropdown-menu sort-menu">
              {sortOptions.map((option) => (
                <button 
                  key={option.key} 
                  className={`dropdown-item ${sortConfig.key === option.key ? 'active' : ''}`}
                  onClick={() => {
                    handleSortClick(option.key);
                    setShowSortMenu(false);
                  }}
                >
                  <span>{option.label}</span>
                  {getSortDirectionIcon(option.key)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {showDataSelector && (
        <DataSelector
          selectedData={selectedData}
          setSelectedData={setSelectedData}
          locationType={locationType}
          variant="table"
        />
      )}

      {showExportActions && (
        <div className="table-actions">
          <ExportActions
            selectedView={selectedView}
            exportContext={exportContext}
            userPlan={userPlan}
            onRequestUpgrade={onRequestUpgrade}
            locationName={selectedLocation?.info?.name || ''}
          />
        </div>
      )}
    </div>
  );
};

export default TableHeader;